import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Permissions as P, type Channel, type ThreadList } from '@comms/shared';
import { displayName, useStore } from '../store';
import { api, uploadFile } from '../lib/api';
import { has, usePermissions } from '../hooks/permissions';
import { Markdown } from '../components/Markdown';
import { Button, ErrorText, Icon, Input, Spinner, cx } from '../components/ui';
import { NavButton } from '../components/MobileNav';

/** A forum: each post is a public thread with an opening message and optional tags. */
export function ForumView({ guildId, channelId }: { guildId: string; channelId: string }) {
  const forum = useStore((s) => s.channels[channelId]);
  const perms = usePermissions(guildId, channelId);
  const [archived, setArchived] = useState(false);
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [list, setList] = useState<ThreadList | null>(null);
  const [composing, setComposing] = useState(false);
  // Refetch when a post in this forum is created or updated.
  const version = useStore((s) =>
    Object.values(s.channels)
      .filter((c) => c.parentId === channelId && c.threadMetadata)
      .map(
        (c) =>
          `${c.id}:${c.threadMetadata!.messageCount}:${c.threadMetadata!.archived}:${c.appliedTags?.join('.')}`,
      )
      .join(','),
  );
  useEffect(() => {
    api<ThreadList>('GET', `/channels/${channelId}/threads?archived=${archived}&limit=100`).then(
      setList,
      () => setList({ threads: [], hasMore: false }),
    );
  }, [channelId, archived, version]);

  if (!forum) return <div className="flex flex-1 items-center justify-center">Not found.</div>;
  const tags = forum.availableTags ?? [];
  const posts = (list?.threads ?? []).filter(
    (t) => !tagFilter || t.appliedTags?.includes(tagFilter),
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-bg-0 px-4 shadow-sm">
        <NavButton />
        <Icon name="forum" className="h-6 w-6 text-text-3" />
        <h1 className="font-semibold text-text-0">{forum.name}</h1>
        {forum.topic && (
          <span className="ml-2 truncate border-l border-bg-3 pl-3 text-sm text-text-3">
            {forum.topic}
          </span>
        )}
      </header>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-3xl space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {has(perms, P.SEND_MESSAGES) && (
              <Button onClick={() => setComposing(!composing)}>
                {composing ? 'Cancel' : 'New Post'}
              </Button>
            )}
            {(['Active', 'Archived'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setArchived(t === 'Archived')}
                className={cx(
                  'rounded px-2 py-1 text-sm',
                  archived === (t === 'Archived') ? 'bg-bg-active text-text-0' : 'text-text-3',
                )}
              >
                {t}
              </button>
            ))}
            {tags.length > 0 && <span className="ml-2 text-xs text-text-3">Filter:</span>}
            {tags.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTagFilter(tagFilter === t.id ? null : t.id)}
                className={cx(
                  'rounded-full px-2 py-0.5 text-sm',
                  tagFilter === t.id ? 'bg-accent text-white' : 'bg-bg-1 text-text-2',
                )}
              >
                {t.emoji} {t.name}
              </button>
            ))}
          </div>
          {composing && (
            <NewPost
              forum={forum}
              guildId={guildId}
              canModerate={has(perms, P.MANAGE_THREADS)}
              canAttach={has(perms, P.ATTACH_FILES)}
            />
          )}
          {!list && <Spinner />}
          {list && posts.length === 0 && (
            <p className="py-8 text-center text-text-3">
              {archived ? 'No archived posts.' : 'No posts yet. Start the conversation!'}
            </p>
          )}
          {posts.map((t) => (
            <PostCard
              key={t.id}
              post={t}
              forum={forum}
              guildId={guildId}
              preview={list?.firstMessages?.[t.id]?.content ?? ''}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PostCard({
  post,
  forum,
  guildId,
  preview,
}: {
  post: Channel;
  forum: Channel;
  guildId: string;
  preview: string;
}) {
  const author = useStore((s) => displayName(s, post.ownerId ?? '', guildId));
  const meta = post.threadMetadata!;
  const replies = Math.max(0, meta.messageCount - 1);
  return (
    <Link
      to={`/channels/${guildId}/${post.id}`}
      className="block rounded-lg bg-bg-1 p-4 hover:bg-bg-hover"
    >
      <div className="mb-1 flex flex-wrap items-center gap-1">
        {(post.appliedTags ?? []).map((id) => {
          const tag = forum.availableTags?.find((t) => t.id === id);
          return tag ? (
            <span key={id} className="rounded-full bg-bg-3 px-2 py-0.5 text-xs text-text-1">
              {tag.emoji} {tag.name}
            </span>
          ) : null;
        })}
        {meta.locked && <Icon name="lock" className="h-3.5 w-3.5 text-text-3" />}
      </div>
      <h3 className="font-semibold text-text-0">{post.name}</h3>
      {preview && (
        <div className="line-clamp-2 text-sm text-text-2">
          <Markdown content={preview} guildId={guildId} />
        </div>
      )}
      <div className="mt-2 flex items-center gap-3 text-xs text-text-3">
        <span>{author}</span>
        <span className="flex items-center gap-1">
          <Icon name="message" className="h-3.5 w-3.5" /> {replies}{' '}
          {replies === 1 ? 'reply' : 'replies'}
        </span>
        <span>{new Date(meta.lastActivityAt).toLocaleString()}</span>
      </div>
    </Link>
  );
}

function NewPost({
  forum,
  guildId,
  canModerate,
  canAttach,
}: {
  forum: Channel;
  guildId: string;
  canModerate: boolean;
  canAttach: boolean;
}) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  const available = (forum.availableTags ?? []).filter((t) => canModerate || !t.moderated);

  async function post() {
    setBusy(true);
    setError(null);
    try {
      const attachmentIds: string[] = [];
      for (const f of files) attachmentIds.push((await uploadFile(f, f.name)).id);
      const res = await api<{ thread: Channel }>('POST', `/channels/${forum.id}/threads`, {
        name: title.trim(),
        message: { content, attachmentIds },
        appliedTags: tags,
      });
      navigate(`/channels/${guildId}/${res.thread.id}`);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 rounded-lg bg-bg-1 p-4">
      <Input
        placeholder="Post title"
        aria-label="Post title"
        value={title}
        maxLength={100}
        onChange={(e) => setTitle(e.target.value)}
        autoFocus
      />
      <textarea
        aria-label="Post message"
        placeholder="What's on your mind?"
        value={content}
        maxLength={4000}
        onChange={(e) => setContent(e.target.value)}
        rows={4}
        className="w-full resize-y rounded bg-bg-0 px-3 py-2 text-text-0 outline-none placeholder:text-text-3 focus:ring-2 focus:ring-accent"
      />
      {available.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {available.map((t) => {
            const on = tags.includes(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() =>
                  setTags(on ? tags.filter((x) => x !== t.id) : [...tags, t.id].slice(0, 5))
                }
                className={cx(
                  'rounded-full px-2 py-0.5 text-sm',
                  on ? 'bg-accent text-white' : 'bg-bg-3 text-text-2',
                )}
              >
                {t.emoji} {t.name}
              </button>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-2">
        {canAttach && (
          <input
            type="file"
            multiple
            onChange={(e) => setFiles([...(e.target.files ?? [])].slice(0, 10))}
            className="text-sm text-text-2"
          />
        )}
        <Button
          className="ml-auto"
          disabled={busy || !title.trim() || (!content.trim() && files.length === 0)}
          onClick={post}
        >
          Post
        </Button>
      </div>
      <ErrorText error={error} />
    </div>
  );
}
